'use client';

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, EyeOff, AlertCircle, Mail, Loader2, Lock, KeyRound } from 'lucide-react';
import { useAuth, type DeviceVerificationInfo } from '@/context/AuthContext';
import { updateMe, verifyDevice, requestLoginCode, verifyLoginCode } from '@/lib/api-client';
import GoogleSignInButton from '@/components/auth/GoogleSignInButton';
import { useGoogleClientId } from '@/components/auth/GoogleAuthWrapper';
import AuthCardHeader from '@/components/auth/AuthCardHeader';

function LoginForm() {
  const { login, loginWithPayload, user: currentUser, refreshUser } = useAuth();
  const googleClientId = useGoogleClientId();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo') ?? '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberUsername, setRememberUsername] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(''); // Track email for resend verification
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deviceVerification, setDeviceVerification] = useState<DeviceVerificationInfo | null>(null);
  const [deviceCode, setDeviceCode] = useState('');
  const [deviceVerifying, setDeviceVerifying] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Login with code state
  const [loginMode, setLoginMode] = useState<'password' | 'code'>('password');
  const [codeEmail, setCodeEmail] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [codeVerifying, setCodeVerifying] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('rememberedUsername');
    if (saved) {
      setUsername(saved);
      setRememberUsername(true);
    }
  }, []);

  // Email-code handlers — shared by the visible buttons AND the form's
  // Enter-key submit. Each guards its own preconditions so Enter is safe.
  const handleSendCode = async () => {
    if (codeSending || !codeEmail.includes('@')) return;
    setCodeSending(true); setError('');
    try { await requestLoginCode(codeEmail); setCodeSent(true); }
    catch { setError('Failed to send code.'); }
    finally { setCodeSending(false); }
  };

  const handleVerifyCode = async () => {
    if (codeVerifying || loginCode.length < 6) return;
    setCodeVerifying(true); setError('');
    try {
      const payload = await verifyLoginCode(codeEmail, loginCode);
      loginWithPayload(payload.user, payload.accessToken, payload.showTrialPopup, payload.trialPopupTitle, payload.trialPopupMessage);
      router.push(returnTo);
    } catch (err) { setError(err instanceof Error ? err.message : 'Invalid or expired code.'); }
    finally { setCodeVerifying(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUnverified(false);

    // In email-code mode the visible buttons are type="button", so a native
    // Enter-key form submit would otherwise fall through to the password
    // checks below and wrongly show "Password is required." Route Enter to
    // the appropriate code action instead.
    if (loginMode === 'code') {
      if (codeSent) void handleVerifyCode(); else void handleSendCode();
      return;
    }

    if (!username.trim()) { setError('Username is required.'); return; }
    if (!password) { setError('Password is required.'); return; }

    if (rememberUsername) {
      localStorage.setItem('rememberedUsername', username);
    } else {
      localStorage.removeItem('rememberedUsername');
    }

    setLoading(true);
    try {
      const result = await login(username, password);
      if (result?.deviceToken) {
        setDeviceVerification(result);
        setLoading(false);
        return;
      }
      // After login, check if password change is required (invited users)
      try {
        const me = await (await import('@/lib/api-client')).getMe();
        if (me.mustChangePassword) {
          setMustChangePassword(true);
          setLoading(false);
          return;
        }
      } catch { /* proceed with redirect */ }
      setRedirecting(true);
      router.push(returnTo);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Invalid username or password.';
      if (msg.includes('verify your email')) {
        setUnverified(true);
      } else if (msg.includes('suspended')) {
        setError('Your account has been suspended. Please contact support.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    try {
      const { resendVerificationByEmail } = await import('@/lib/api-client');
      await resendVerificationByEmail(unverifiedEmail);
      setResendMsg('Verification email sent! Check your inbox.');
    } catch {
      setResendMsg('Could not resend. Please try registering again.');
    } finally {
      setResending(false);
    }
  };

  // Already logged in — but skip if redirect is in progress (avoids flash after login)
  if (currentUser && !mustChangePassword && !redirecting) {
    return (
      <div className="text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto">
          <Lock size={24} className="text-sky-400" />
        </div>
        <h2 className="text-lg font-semibold text-white">Already signed in</h2>
        <p className="text-sm text-fg-muted">You're logged in as <strong className="text-white">@{currentUser.username}</strong></p>
        <div className="space-y-2 pt-2">
          <button onClick={() => router.push(returnTo)}
            className="w-full bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold py-3 rounded-lg transition-colors">
            Continue to site
          </button>
          <button onClick={async () => { await (await import('@/lib/api-client')).logout(); window.location.reload(); }}
            className="w-full border border-default hover:border-muted text-fg-muted hover:text-white text-sm py-3 rounded-lg transition-colors">
            Sign out and switch accounts
          </button>
        </div>
      </div>
    );
  }

  if (mustChangePassword) {
    const handleChangePassword = async (e: React.FormEvent) => {
      e.preventDefault();
      if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
      if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
      setChangingPassword(true);
      setError('');
      try {
        await updateMe({ password: newPassword });
        await refreshUser();
        router.push(returnTo);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to change password');
      } finally { setChangingPassword(false); }
    };

    return (
      <div className="space-y-4">
        <div className="text-center mb-2">
          <div className="w-14 h-14 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto mb-3">
            <Lock size={24} className="text-sky-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Set your password</h2>
          <p className="text-sm text-fg-subtle mt-1">Please choose a new password to continue.</p>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-lg">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters" autoFocus
              className="w-full bg-surface border border-default rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat your password"
              className="w-full bg-surface border border-default rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50" />
          </div>
          <button type="submit" disabled={changingPassword || newPassword.length < 6 || newPassword !== confirmPassword}
            className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
            {changingPassword ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    );
  }

  if (deviceVerification) {
    const handleVerifyDevice = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!deviceCode.trim()) { setDeviceError('Please enter the verification code.'); return; }
      setDeviceVerifying(true);
      setDeviceError('');
      try {
        const payload = await verifyDevice(deviceVerification.deviceToken, deviceCode);
        loginWithPayload(payload.user, payload.accessToken, payload.showTrialPopup, payload.trialPopupTitle, payload.trialPopupMessage);
        router.push(returnTo);
      } catch (err) {
        setDeviceError(err instanceof Error ? err.message : 'Verification failed.');
      } finally {
        setDeviceVerifying(false);
      }
    };

    const handleResendDeviceCode = async () => {
      setDeviceError('');
      setDeviceCode('');
      setDeviceVerification(null);
      // Re-trigger login to generate a new code
      setLoading(true);
      try {
        const result = await login(username, password);
        if (result?.deviceToken) {
          setDeviceVerification(result);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to resend code.');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto">
          <Lock size={24} className="text-sky-400" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white mb-1">New Device Detected</h2>
          <p className="text-sm text-fg-muted">
            A verification code has been sent to <span className="text-white font-medium">{deviceVerification.email}</span>
          </p>
        </div>
        <form onSubmit={handleVerifyDevice} className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={deviceCode}
            onChange={(e) => setDeviceCode(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter 6-digit code"
            className="w-full text-center text-2xl font-mono tracking-[0.3em] bg-surface border border-default rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
            autoFocus
          />
          {deviceError && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle size={14} /> {deviceError}
            </div>
          )}
          <button
            type="submit"
            disabled={deviceVerifying || deviceCode.length < 6}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
          >
            {deviceVerifying && <Loader2 size={14} className="animate-spin" />}
            {deviceVerifying ? 'Verifying...' : 'Verify Device'}
          </button>
        </form>
        <button
          onClick={handleResendDeviceCode}
          disabled={loading}
          className="text-sm text-fg-subtle hover:text-sky-400 transition-colors"
        >
          Resend code
        </button>
        <button
          onClick={() => { setDeviceVerification(null); setDeviceCode(''); setDeviceError(''); }}
          className="w-full py-2.5 border border-default hover:border-muted text-fg-muted hover:text-white text-sm rounded-xl transition-colors"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (unverified) {
    return (
      <div className="text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
          <Mail size={24} className="text-amber-400" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white mb-1">Email not verified</h2>
          <p className="text-sm text-fg-muted">
            Please verify your email address before signing in. Check your inbox for the verification link.
          </p>
        </div>
        <button
          onClick={handleResend}
          disabled={resending || !!resendMsg}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-medium rounded-xl transition-colors"
        >
          {resending && <Loader2 size={14} className="animate-spin" />}
          {resendMsg ? resendMsg : resending ? 'Sending…' : 'Resend verification email'}
        </button>
        <button
          onClick={() => setUnverified(false)}
          className="w-full py-2.5 border border-default hover:border-muted text-fg-muted hover:text-white text-sm rounded-xl transition-colors"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
          <AlertCircle size={16} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {loginMode === 'password' ? (
        <>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your username"
              autoComplete="username"
              className="w-full bg-surface border border-default focus:border-sky-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 text-sm transition-all"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-300">Password</label>
              <div className="flex gap-3">
                <Link href="/forgot-username" className="text-xs text-fg-subtle hover:text-fg transition-colors">
                  Forgot username?
                </Link>
                <Link href="/forgot-password" className="text-xs text-sky-400 hover:text-sky-300 transition-colors">
                  Forgot password?
                </Link>
              </div>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                className="w-full bg-surface border border-default focus:border-sky-500 rounded-xl px-4 py-3 pr-10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 text-sm transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-fg transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberUsername}
              onChange={(e) => setRememberUsername(e.target.checked)}
              className="w-4 h-4 rounded border-default bg-surface text-sky-500 focus:ring-sky-500/30 focus:ring-2 accent-sky-500"
            />
            <span className="text-sm text-fg-muted">Remember my username</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-500/50 text-white font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Signing in...</>
            ) : (
              'Sign In'
            )}
          </button>

          <button type="button" onClick={() => { setLoginMode('code'); setError(''); }}
            className="w-full text-center text-xs text-fg-subtle hover:text-sky-400 transition-colors flex items-center justify-center gap-1.5 py-1">
            <KeyRound size={12} /> Sign in with email code instead
          </button>
        </>
      ) : (
        <>
          {!codeSent ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Email Address</label>
                <input type="email" value={codeEmail} onChange={(e) => setCodeEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email"
                  className="w-full bg-surface border border-default focus:border-sky-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 text-sm transition-all" />
                <p className="text-[11px] text-slate-600 mt-1">We&apos;ll send a 6-digit login code to your email.</p>
              </div>
              <button type="button" disabled={codeSending || !codeEmail.includes('@')} onClick={() => void handleSendCode()} className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-500/50 text-white font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
                {codeSending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send Login Code
              </button>
            </>
          ) : (
            <>
              <div className="text-center text-sm text-fg-muted mb-2">
                Code sent to <strong className="text-white">{codeEmail}</strong>
                <p className="text-[11px] text-slate-600 mt-0.5">Valid for 15 minutes</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Login Code</label>
                <input type="text" value={loginCode} onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456" autoComplete="one-time-code" inputMode="numeric" maxLength={6} autoFocus
                  className="w-full bg-surface border border-default focus:border-sky-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 text-sm transition-all text-center text-lg tracking-[0.3em] font-mono" />
              </div>
              <button type="button" disabled={codeVerifying || loginCode.length < 6} onClick={() => void handleVerifyCode()} className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-500/50 text-white font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
                {codeVerifying ? <Loader2 size={14} className="animate-spin" /> : null} Verify & Sign In
              </button>
              <button type="button" onClick={() => { setCodeSent(false); setLoginCode(''); }}
                className="w-full text-center text-xs text-fg-subtle hover:text-fg transition-colors py-1">
                Resend code
              </button>
            </>
          )}
          <button type="button" onClick={() => { setLoginMode('password'); setError(''); setCodeSent(false); setLoginCode(''); }}
            className="w-full text-center text-xs text-fg-subtle hover:text-sky-400 transition-colors flex items-center justify-center gap-1.5 py-1">
            <Lock size={12} /> Sign in with username & password instead
          </button>
        </>
      )}

      {googleClientId && (
        <>
          <div className="relative flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border-default" />
            <span className="text-xs text-slate-600">or continue with</span>
            <div className="flex-1 h-px bg-border-default" />
          </div>
          <GoogleSignInButton
            returnTo={returnTo}
            onError={(msg) => setError(msg)}
            label="Continue with Google"
          />
        </>
      )}
    </form>
  );
}

export default function LoginPage() {

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-surface">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card-bg border border-default rounded-2xl p-8">
          <AuthCardHeader
            title="Sign in to your account"
            subtitle="Welcome back! Enter your username to continue."
          />

          <Suspense fallback={<div className="text-center text-fg-subtle text-sm">Loading...</div>}>
            <LoginForm />
          </Suspense>

          <p className="text-center text-sm text-fg-subtle mt-6">
            Don&apos;t have an account?{' '}
            <Link href="/create-account" className="text-sky-400 hover:text-sky-300 font-medium transition-colors">
              Create one free
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-slate-600 mt-4">
          By signing in, you agree to our{' '}
          <Link href="/terms" className="hover:text-fg-muted transition-colors">Terms</Link>
          {' '}and{' '}
          <Link href="/privacy" className="hover:text-fg-muted transition-colors">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
